import { useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react'
import { AlertTriangle, Loader2, Plus, Trash2 } from 'lucide-react'
import { subscribeToAttributes } from '@/features/materials/categories-api'
import { CategoryPicker } from '@/features/materials/CategoryPicker'
import { MaterialPicker } from '@/features/materials/MaterialPicker'
import { categoryLabel } from '@/lib/category-groups'
import { CategoryIndex } from '@/lib/category-ref'
import {
  buildFactKeyPalette,
  groupScopedQuestions,
  insertAtCursor,
  resolveSectionQuestions,
  type FactKeyPalette,
} from '@/features/rules/fact-key-palette'
import { AttributePalette, KeyPalette } from '@/features/rules/LogicPalette'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input, Select } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { TranslatedInput } from '@/components/TranslatedField'
import { sanitizeTranslations as sanitize } from '@/lib/utils'
import {
  replaceQuestionChildren,
  saveQuestion,
  type EffectDraft,
  type OptionDraft,
} from '@/features/rules/rules-api'
import { describeWriteError } from '@/features/users/permissions'
import {
  ANSWER_TYPES,
  ANSWER_TYPE_LABELS,
  ANSWER_TYPE_NOTES,
  CATEGORY_ANSWER_TYPES,
  FIELD_TYPES,
  OPTION_KIND,
  TRANSLATION_LANGUAGES,
  UNITLESS_ANSWER_TYPES,
} from '@/lib/constants'
import type {
  AnswerType,
  ConfigQuestion,
  Material,
  MaterialCategory,
  MaterialCategoryAttribute,
  MaterialEffect,
  QuestionGroup,
  QuestionOption,
  SectionType,
} from '@/lib/types'

type OptionRow = {
  value: string
  label: string
  labelI18n: Record<string, string>
  fieldType: AnswerType
  /** Edited by name (the category's CURRENT name); the id is resolved on save. */
  materialCategory: string
  /** The category reference as loaded ('' for a new row): kept when the name cannot be resolved. */
  originalCategoryId: string
  originalCategory: string
  /** The key as loaded ('' for a new row): lets a renamed material field keep its effects. */
  originalKey: string
  /** UI only: the category was picked in this dialog, so it is no longer re-derived from the stored one. */
  categoryTouched?: boolean
}

type EffectRow = {
  materialId: string
  expression: string
  targetFieldKey: string
  /** See `MaterialEffect.fixedMaterial`: a fixed cost item added together with the picked material. */
  fixedMaterial: boolean
}

/** A material (CHOICE) field of a REPEATER schema that an effect may target. */
type RepeaterMaterialField = { key: string; label: string; category: string }

/** The `{attr:CODE}` chips an effect may use: the attributes of the category it targets. */
type EffectAttributes = { category: string; items: MaterialCategoryAttribute[] }

/**
 * The field an effect targets: the one named by `key`, or — blank / no longer in the schema — the
 * first material field. Mirrors the fallback of the app's engine (`AutomatedMaterials.kt`).
 */
function resolveTargetField(
  fields: RepeaterMaterialField[],
  key: string,
): RepeaterMaterialField | undefined {
  return fields.find((field) => field.key === key) ?? fields[0]
}

/**
 * The question editor: the question itself plus its options and material effects.
 *
 * Options and effects are saved together with the question in ONE atomic batch
 * (`replaceQuestionChildren`), so a half-applied edit cannot leave a room with a mix of old and new
 * logic. Order indexes are assigned by position in the list, which is how the app reads them.
 */
export function QuestionEditDialog({
  question,
  owner,
  options,
  effects,
  categories,
  materials,
  nextOrder,
  allQuestions,
  allOptions,
  groups,
  sectionTypes,
  onClose,
}: {
  /** `null` = create a new question. */
  question: ConfigQuestion | null
  /** Where the question belongs: a section type or a group. */
  owner: { kind: 'section'; id: string } | { kind: 'group'; id: string }
  options: QuestionOption[]
  effects: MaterialEffect[]
  categories: MaterialCategory[]
  materials: Material[]
  nextOrder: number
  /** The whole rules config, for the fact-key palette of the logic boxes. */
  allQuestions: ConfigQuestion[]
  allOptions: QuestionOption[]
  groups: QuestionGroup[]
  sectionTypes: SectionType[]
  onClose: () => void
}) {
  const [factKey, setFactKey] = useState(question?.factKey ?? '')
  const [prompt, setPrompt] = useState(question?.prompt ?? '')
  const [promptI18n, setPromptI18n] = useState<Record<string, string>>(question?.promptI18n ?? {})
  const [answerType, setAnswerType] = useState<AnswerType>(question?.answerType ?? 'NUMBER')
  const [unit, setUnit] = useState(question?.unit ?? '')
  // Categories are linked by id; the editor works with their CURRENT names (a renamed category shows
  // its new name) and resolves the id again on save (`resolveCategoryId`). Names are derived at
  // render, so they are right even when the catalog arrives after the dialog opened.
  const categoryIndex = useMemo(() => new CategoryIndex(categories), [categories])
  /** `null` = not changed in this dialog: shows the stored reference under its current name. */
  const [categoryEdit, setMaterialCategory] = useState<string | null>(null)
  const materialCategory =
    categoryEdit ??
    (question ? categoryIndex.displayName(question.materialCategoryId, question.materialCategory) : '')
  const [displayCondition, setDisplayCondition] = useState(question?.displayCondition ?? '')
  const [order, setOrder] = useState(String(question?.orderIndex ?? nextOrder))
  const [rawOptionRows, setOptionRows] = useState<OptionRow[]>(
    options.map((item) => ({
      value: item.value,
      label: item.label,
      labelI18n: item.labelI18n,
      fieldType: item.fieldType,
      materialCategory: item.materialCategory,
      originalCategoryId: item.materialCategoryId,
      originalCategory: item.materialCategory,
      originalKey: item.value,
    })),
  )
  // A field whose category was not changed here shows its stored reference under the current name.
  const optionRows = useMemo(
    () =>
      rawOptionRows.map((row) =>
        !row.categoryTouched && row.materialCategory === row.originalCategory
          ? {
              ...row,
              materialCategory: categoryIndex.displayName(row.originalCategoryId, row.originalCategory),
            }
          : row,
      ),
    [rawOptionRows, categoryIndex],
  )
  const [effectRows, setEffectRows] = useState<EffectRow[]>(
    effects.map((item) => ({
      materialId: item.materialId,
      expression: item.expression,
      targetFieldKey: item.targetFieldKey,
      fixedMaterial: item.fixedMaterial,
    })),
  )
  const [isSaving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const optionKind = OPTION_KIND[answerType]
  /** REPEATER and QUESTION_LIST share the schema of fields (`hasFieldSchema` in the app). */
  const isRepeater = answerType === 'REPEATER' || answerType === 'QUESTION_LIST'
  /** CHOICE and MULTI_CHOICE are answered by picking material(s) from this category. */
  const usesCategory = CATEGORY_ANSWER_TYPES.includes(answerType)
  const showUnit = !UNITLESS_ANSWER_TYPES.includes(answerType)
  /**
   * KEY_SET has no answer of its own: every item carries its own fact key. FACT is not answered at
   * all (effects only), so it has no fact key either.
   */
  const isFact = answerType === 'FACT'
  const needsFactKey = answerType !== 'KEY_SET' && !isFact
  /**
   * REPEATER / QUESTION_LIST only: its material (CHOICE) fields with a key, in schema order (keys
   * trimmed exactly as they are saved). With TWO or more, each effect picks the field whose selected
   * material it targets (`targetFieldKey`); with one — and for every other answer type — nothing
   * changes.
   */
  const repeaterMaterialFields: RepeaterMaterialField[] = isRepeater
    ? optionRows
        .filter((row) => row.fieldType === 'CHOICE' && row.value.trim().length > 0)
        .map((row) => ({
          key: row.value.trim(),
          label: row.label.trim() || row.value.trim(),
          category: row.materialCategory.trim(),
        }))
    : []
  /** The category of the first material field (the default target), for the labels. */
  const repeaterCategory = repeaterMaterialFields[0]?.category ?? ''
  /**
   * Effects of these questions apply to the material(s) the user picks during the interview, not to a
   * fixed one, so the material picker is replaced by a label (mirrors `effectTargetsSelectedMaterial`).
   * A repeater qualifies as soon as it has a material field with a key — as in the engine, which
   * targets the field by its key; its category may still be blank (it used to be required here, so a
   * first field without a category hid the field choice and the cost-item effect).
   */
  const effectTargetsSelectedMaterial = usesCategory || repeaterMaterialFields.length > 0
  const effectCategoryName = usesCategory ? materialCategory : repeaterCategory
  const pickTargetField = effectTargetsSelectedMaterial && repeaterMaterialFields.length >= 2
  /**
   * A material field whose key was edited in this dialog keeps its effects: an effect's stored key is
   * translated old → new before it is resolved (otherwise it would fall back to the first field).
   */
  const renamedKeys = new Map(
    optionRows
      .filter(
        (row) =>
          row.fieldType === 'CHOICE' &&
          row.originalKey.length > 0 &&
          row.originalKey !== row.value.trim(),
      )
      .map((row) => [row.originalKey, row.value.trim()] as const),
  )
  const currentTargetKey = (key: string) => renamedKeys.get(key) ?? key
  const canSave =
    prompt.trim().length > 0 &&
    (!needsFactKey || factKey.trim().length > 0) &&
    (!usesCategory || materialCategory.trim().length > 0)

  /**
   * Fact keys for the logic boxes, built like the app's question editor: a group question sees every
   * group question, a section question sees its room's effective questions (global + selected groups
   * + its own). The saved copy of THIS question is replaced by the unsaved draft, so a new or renamed
   * key (and a KEY_SET/REPEATER item just typed) is offered immediately — effects need it.
   */
  const palette: FactKeyPalette = useMemo(() => {
    const scoped =
      owner.kind === 'group'
        ? groupScopedQuestions(allQuestions)
        : resolveSectionQuestions(
            sectionTypes.find((item) => item.id === owner.id) ?? null,
            owner.id,
            allQuestions,
            groups,
          )
    const draftId = question?.id ?? '__draft__'
    const draft: ConfigQuestion = {
      id: draftId,
      sectionTypeId: owner.kind === 'section' ? owner.id : '',
      groupId: owner.kind === 'group' ? owner.id : '',
      factKey: needsFactKey ? factKey.trim() : '',
      prompt: '',
      promptI18n: {},
      answerType,
      unit: '',
      materialCategory: '',
      materialCategoryId: '',
      orderIndex: 0,
      displayCondition: '',
    }
    const optionsByQuestion = new Map<string, QuestionOption[]>()
    for (const option of allOptions) {
      const list = optionsByQuestion.get(option.questionId)
      if (list) list.push(option)
      else optionsByQuestion.set(option.questionId, [option])
    }
    optionsByQuestion.set(
      draftId,
      optionRows.map((row, index) => ({
        id: `${draftId}-${index}`,
        questionId: draftId,
        value: row.value,
        label: row.label,
        labelI18n: {},
        orderIndex: index,
        fieldType: row.fieldType,
        materialCategory: row.materialCategory,
        materialCategoryId: '',
      })),
    )
    // The question's own keys come first, as in the app.
    const questions = [draft, ...scoped.filter((item) => item.id !== draftId)]
    return buildFactKeyPalette(questions, groups, optionsByQuestion)
  }, [
    owner,
    allQuestions,
    sectionTypes,
    groups,
    allOptions,
    question?.id,
    needsFactKey,
    factKey,
    answerType,
    optionRows,
  ])

  // Attribute chips ({attr:CODE}), resolved from the picked category NAME (the editor's current
  // names; see `CategoryIndex`):
  //  - CHOICE / MULTI_CHOICE: the question's material category;
  //  - REPEATER / QUESTION_LIST: the category of the material field the effect targets (per effect),
  //    so the chips appear only once that field has a category.
  const categoryIdByName = (name: string) => categoryIndex.idForName(name)
  /**
   * The id to store for a category picked by `name`. A name the catalog does not know (legacy free
   * text, a deleted category) keeps the id loaded with it, so a save never drops a working link.
   */
  const resolveCategoryId = (name: string, previousId: string, previousName: string) => {
    const trimmed = name.trim()
    const keep =
      trimmed === previousName.trim() ||
      trimmed === categoryIndex.displayName(previousId, previousName).trim()
    return categoryIndex.idForName(trimmed, keep ? previousId : '')
  }
  /** The name copy stored next to an id: the category's current name. */
  const storedCategoryName = (categoryId: string, name: string) =>
    categoryIndex.displayName(categoryId, name.trim()).trim()
  const attributeCategoryNames = usesCategory
    ? [materialCategory.trim()]
    : effectTargetsSelectedMaterial
      ? repeaterMaterialFields.map((field) => field.category)
      : []
  // A stable key of the category ids to listen to (one live subscription per category).
  const attributeCategoryKey = [
    ...new Set(attributeCategoryNames.map(categoryIdByName).filter((id) => id.length > 0)),
  ]
    .sort()
    .join('|')
  const [attributesByCategoryId, setAttributesByCategoryId] = useState<
    Record<string, MaterialCategoryAttribute[]>
  >({})
  useEffect(() => {
    if (!attributeCategoryKey) return
    const unsubscribes = attributeCategoryKey.split('|').map((categoryId) =>
      subscribeToAttributes(
        categoryId,
        (items) => setAttributesByCategoryId((previous) => ({ ...previous, [categoryId]: items })),
        () => setAttributesByCategoryId((previous) => ({ ...previous, [categoryId]: [] })),
      ),
    )
    return () => unsubscribes.forEach((unsubscribe) => unsubscribe())
  }, [attributeCategoryKey])
  /** The category whose attributes an effect may read, and those attributes. */
  const effectAttributes = (row: EffectRow | undefined): EffectAttributes => {
    if (!row || !effectTargetsSelectedMaterial) return { category: '', items: [] }
    // A repeater's fixed cost item names no field, so it reads no `{attr:}` (as in the engine).
    if (row.fixedMaterial && isRepeater) return { category: '', items: [] }
    const category = usesCategory
      ? materialCategory.trim()
      : (resolveTargetField(repeaterMaterialFields, currentTargetKey(row.targetFieldKey))?.category ??
        '')
    const categoryId = categoryIdByName(category)
    return { category, items: categoryId ? (attributesByCategoryId[categoryId] ?? []) : [] }
  }

  const conditionRef = useRef<HTMLInputElement>(null)
  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      const questionCategoryId = usesCategory
        ? resolveCategoryId(
            materialCategory,
            question?.materialCategoryId ?? '',
            question?.materialCategory ?? '',
          )
        : ''
      const id = await saveQuestion(question?.id ?? null, {
        sectionTypeId: owner.kind === 'section' ? owner.id : '',
        groupId: owner.kind === 'group' ? owner.id : '',
        // FACT stores no answer: never a fact key (it must not reach the palette) and no unit.
        factKey: isFact ? '' : factKey.trim(),
        prompt: prompt.trim(),
        promptI18n: sanitize(promptI18n),
        answerType,
        unit: isFact ? '' : unit.trim(),
        materialCategory: usesCategory ? storedCategoryName(questionCategoryId, materialCategory) : '',
        materialCategoryId: questionCategoryId,
        orderIndex: Number.parseInt(order, 10) || 0,
        displayCondition: displayCondition.trim(),
      })

      const optionDrafts: OptionDraft[] = optionKind
        ? optionRows
            .filter((row) => row.value.trim().length > 0 || row.label.trim().length > 0)
            .map((row, index) => {
              const hasCategory = isRepeater && row.fieldType === 'CHOICE'
              const categoryId = hasCategory
                ? resolveCategoryId(row.materialCategory, row.originalCategoryId, row.originalCategory)
                : ''
              return {
                questionId: id,
                value: row.value.trim(),
                label: row.label.trim(),
                labelI18n: sanitize(row.labelI18n),
                orderIndex: index,
                fieldType: isRepeater ? row.fieldType : 'TEXT',
                materialCategory: hasCategory ? storedCategoryName(categoryId, row.materialCategory) : '',
                materialCategoryId: categoryId,
              }
            })
        : []

      const effectDrafts: EffectDraft[] = effectRows
        .filter((row) => row.expression.trim().length > 0)
        .map((row) => ({
          questionId: id,
          // When the target is chosen during the interview the engine ignores `materialId`, so the
          // stored value is kept as-is instead of being wiped (the app does the same).
          materialId: row.materialId,
          expression: row.expression.trim(),
          // Picker shown → store the field it shows (a stale key resolves to the first field, which
          // is also what the engine would use). Otherwise the stored key is kept untouched.
          targetFieldKey:
            row.fixedMaterial && isRepeater
              ? '' // names no field (see the engine)
              : pickTargetField
                ? (resolveTargetField(repeaterMaterialFields, currentTargetKey(row.targetFieldKey))
                    ?.key ?? '')
                : row.targetFieldKey,
          // Kept as stored; the engine reads it only where the target is a picked material.
          fixedMaterial: row.fixedMaterial,
        }))

      await replaceQuestionChildren(id, optionDrafts, effectDrafts)
      onClose()
    } catch (caught) {
      setError(describeWriteError(caught))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{question ? 'Вопрос' : 'Новый вопрос'}</DialogTitle>
          <DialogDescription>
            {owner.kind === 'section'
              ? 'Вопрос принадлежит типу раздела.'
              : 'Вопрос принадлежит группе и попадает во все помещения, где группа применяется.'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <label className="grid gap-1.5 text-sm">
            <span className="font-medium">Текст вопроса (ru)</span>
            <Input value={prompt} onChange={(event) => setPrompt(event.target.value)} autoFocus />
          </label>

          <div className="grid gap-2 rounded-lg border p-3">
            <span className="text-sm font-medium">Переводы текста</span>
            {TRANSLATION_LANGUAGES.map((language) => (
              <TranslatedInput
                key={language.tag}
                value={promptI18n[language.tag] ?? ''}
                onChange={(event) =>
                  setPromptI18n({ ...promptI18n, [language.tag]: event.target.value })
                }
                source={prompt}
                lang={language.tag}
                onTranslated={(text) =>
                  setPromptI18n((prev) => ({ ...prev, [language.tag]: text }))
                }
                placeholder={language.label}
                aria-label={`Текст вопроса (${language.label})`}
              />
            ))}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {needsFactKey ? (
              <label className="grid gap-1.5 text-sm">
                <span className="font-medium">Ключ факта</span>
                <Input
                  value={factKey}
                  onChange={(event) => setFactKey(event.target.value)}
                  className="font-mono"
                  placeholder="area"
                />
                <span className="text-muted-foreground text-xs">
                  На него ссылаются формулы и условия: <code>{'{area}'}</code>.
                </span>
              </label>
            ) : isFact ? (
              <p className="text-muted-foreground self-center text-xs">
                У факта нет ответа и ключа факта: он только добавляет пункты своих эффектов.
              </p>
            ) : (
              <p className="text-muted-foreground self-center text-xs">
                У набора ключей нет своего ответа: ключ факта задаёт каждый элемент набора отдельно.
              </p>
            )}

            <label className="grid gap-1.5 text-sm">
              <span className="font-medium">Тип ответа</span>
              <Select
                value={answerType}
                onChange={(event) => setAnswerType(event.target.value as AnswerType)}
              >
                {ANSWER_TYPES.map((value) => (
                  <option key={value} value={value}>
                    {ANSWER_TYPE_LABELS[value]}
                  </option>
                ))}
              </Select>
              <span className="text-muted-foreground text-xs">{ANSWER_TYPE_NOTES[answerType]}</span>
            </label>

            {showUnit ? (
              <label className="grid gap-1.5 text-sm">
                <span className="font-medium">Единица (подсказка)</span>
                <Input
                  value={unit}
                  onChange={(event) => setUnit(event.target.value)}
                  placeholder="м²"
                />
              </label>
            ) : null}

            <label className="grid gap-1.5 text-sm">
              <span className="font-medium">Порядок</span>
              <Input type="number" value={order} onChange={(event) => setOrder(event.target.value)} />
            </label>

            {usesCategory ? (
              // Not a <label>: a click inside the open list would re-activate the picker's button.
              <div className="grid gap-1.5 text-sm sm:col-span-2">
                <span id="question-category-label" className="font-medium">
                  Категория материалов
                </span>
                <CategoryNamePicker
                  categories={categories}
                  value={materialCategory}
                  onChange={setMaterialCategory}
                  labelledBy="question-category-label"
                />
                <span className="text-muted-foreground text-xs">
                  Вариантами в опросе будут материалы из этой категории.
                  {answerType === 'MULTI_CHOICE'
                    ? ' Пользователь выбирает несколько материалов и количество для каждого; эффекты ниже применяются к каждому выбранному.'
                    : ' Эффекты ниже применяются к выбранному материалу.'}
                </span>
                {materialCategory.trim().length === 0 ? (
                  <span className="text-destructive text-xs">
                    Без категории вопрос не сохранить: выбирать будет не из чего.
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="grid gap-2 text-sm">
            <label htmlFor="question-display-condition" className="font-medium">
              {isFact ? 'Применять только при условии' : 'Условие показа'}
            </label>
            <KeyPalette
              palette={palette}
              onInsert={(token) =>
                insertAtCursor(conditionRef.current, displayCondition, token, setDisplayCondition)
              }
            />
            <Input
              id="question-display-condition"
              ref={conditionRef}
              value={displayCondition}
              onChange={(event) => setDisplayCondition(event.target.value)}
              placeholder='{wall_type} == "wallpaper"'
              className="font-mono"
            />
            <span className="text-muted-foreground text-xs">
              {isFact
                ? 'Пусто — эффекты добавляются всегда. Ошибка в выражении трактуется как «применять» (fail-open, как у условия показа).'
                : 'Пусто — вопрос показывается всегда. Ошибка в выражении трактуется как «показать» (fail-open), чтобы опечатка не спрятала вопрос.'}
            </span>
          </div>

          {optionKind ? (
            <OptionsEditor
              kind={optionKind}
              isRepeater={isRepeater}
              rows={optionRows}
              onRows={setOptionRows}
              categories={categories}
            />
          ) : null}

          <EffectsEditor
            rows={effectRows}
            onRows={setEffectRows}
            materials={materials}
            targetIsChosenMaterial={effectTargetsSelectedMaterial}
            categoryName={effectCategoryName}
            fansOverSelection={answerType === 'MULTI_CHOICE'}
            palette={palette}
            attributesFor={effectAttributes}
            targetFields={pickTargetField ? repeaterMaterialFields : []}
            currentTargetKey={currentTargetKey}
            fieldSchema={isRepeater}
          />

          {error ? (
            <Alert variant="destructive">
              <AlertTriangle />
              <AlertTitle>Не сохранено</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Отмена
          </Button>
          <Button onClick={() => void handleSave()} disabled={isSaving || !canSave}>
            {isSaving ? <Loader2 className="animate-spin" /> : null}
            Сохранить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function OptionsEditor({
  kind,
  isRepeater,
  rows,
  onRows,
  categories,
}: {
  kind: string
  isRepeater: boolean
  rows: OptionRow[]
  onRows: Dispatch<SetStateAction<OptionRow[]>>
  categories: MaterialCategory[]
}) {
  const update = (index: number, patch: Partial<OptionRow>) =>
    onRows(rows.map((row, position) => (position === index ? { ...row, ...patch } : row)))
  // Async (machine translation): patch the LATEST rows, so another answer or an edit meanwhile is kept.
  const setLabelTranslation = (index: number, tag: string, text: string) =>
    onRows((current) =>
      current.map((row, position) =>
        position === index ? { ...row, labelI18n: { ...row.labelI18n, [tag]: text } } : row,
      ),
    )

  return (
    <div className="grid gap-3 rounded-lg border p-3">
      <span className="text-sm font-medium">{kind}</span>
      {rows.length === 0 ? (
        <p className="text-muted-foreground text-xs">Пока пусто — порядок задаётся порядком строк.</p>
      ) : null}

      {rows.map((row, index) => (
        <div key={index} className="grid gap-2 rounded-md border p-2">
          <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
            <Input
              value={row.label}
              onChange={(event) => update(index, { label: event.target.value })}
              placeholder="Название"
              aria-label={`Название варианта ${index + 1}`}
            />
            <Input
              value={row.value}
              onChange={(event) => update(index, { value: event.target.value })}
              placeholder={isRepeater ? 'ключ поля' : 'value'}
              className="font-mono"
              aria-label={`Значение варианта ${index + 1}`}
            />
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={() => onRows(rows.filter((_, position) => position !== index))}
              aria-label={`Удалить вариант ${index + 1}`}
            >
              <Trash2 />
            </Button>
          </div>

          {isRepeater ? (
            <div className="grid gap-2 sm:grid-cols-2">
              <Select
                value={row.fieldType}
                onChange={(event) => update(index, { fieldType: event.target.value as AnswerType })}
                aria-label={`Тип поля ${index + 1}`}
              >
                {FIELD_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {ANSWER_TYPE_LABELS[type]}
                  </option>
                ))}
              </Select>
              {row.fieldType === 'CHOICE' ? (
                <CategoryNamePicker
                  categories={categories}
                  value={row.materialCategory}
                  onChange={(name) => update(index, { materialCategory: name, categoryTouched: true })}
                  ariaLabel={`Категория поля ${index + 1}`}
                />
              ) : null}
            </div>
          ) : null}

          <div className="grid gap-2 sm:grid-cols-2">
            {TRANSLATION_LANGUAGES.map((language) => (
              <TranslatedInput
                key={language.tag}
                value={row.labelI18n[language.tag] ?? ''}
                source={row.label}
                lang={language.tag}
                onTranslated={(text) => setLabelTranslation(index, language.tag, text)}
                onChange={(event) =>
                  update(index, {
                    labelI18n: { ...row.labelI18n, [language.tag]: event.target.value },
                  })
                }
                placeholder={`Название · ${language.label}`}
                aria-label={`Название варианта ${index + 1} (${language.label})`}
              />
            ))}
          </div>
        </div>
      ))}

      <Button
        variant="outline"
        size="sm"
        onClick={() =>
          onRows([
            ...rows,
            {
              value: '',
              label: '',
              labelI18n: {},
              fieldType: 'TEXT',
              materialCategory: '',
              originalCategoryId: '',
              originalCategory: '',
              originalKey: '',
            },
          ])
        }
      >
        <Plus />
        Добавить
      </Button>
    </div>
  )
}

function EffectsEditor({
  rows,
  onRows,
  materials,
  targetIsChosenMaterial,
  categoryName,
  fansOverSelection,
  palette,
  attributesFor,
  targetFields,
  currentTargetKey,
  fieldSchema,
}: {
  rows: EffectRow[]
  onRows: (rows: EffectRow[]) => void
  materials: Material[]
  /** The target is picked during the interview, so no fixed material is chosen here. */
  targetIsChosenMaterial: boolean
  categoryName: string
  /** MULTI_CHOICE: the same expression runs for every selected material. */
  fansOverSelection: boolean
  palette: FactKeyPalette
  /**
   * The attributes an effect may reference: the question's category (CHOICE / MULTI_CHOICE) or the
   * category of the effect's target material field (REPEATER / QUESTION_LIST); otherwise empty.
   */
  attributesFor: (row: EffectRow | undefined) => EffectAttributes
  /** REPEATER with 2+ material fields: the fields an effect may target; empty = no picker. */
  targetFields: RepeaterMaterialField[]
  /** Translates a stored key whose field was renamed in this dialog to its current key. */
  currentTargetKey: (key: string) => string
  /** REPEATER / QUESTION_LIST: a fixed cost item rides along with any pick of the entry. */
  fieldSchema: boolean
}) {
  const update = (index: number, patch: Partial<EffectRow>) =>
    onRows(rows.map((row, position) => (position === index ? { ...row, ...patch } : row)))

  // One shared palette for all effect rows: it inserts into the expression focused last (the last
  // row until one is focused), and the target is named next to it.
  const expressionRefs = useRef<(HTMLTextAreaElement | null)[]>([])
  const [focusedRow, setFocusedRow] = useState<number | null>(null)
  const targetRow = focusedRow !== null && focusedRow < rows.length ? focusedRow : rows.length - 1
  // The attribute chips follow that row's target (a repeater effect reads the attributes of the
  // material picked in ITS field), so changing the field below swaps the chips.
  const targetAttributes = attributesFor(rows[targetRow])
  const insert = (token: string) => {
    const row = rows[targetRow]
    if (!row) return
    insertAtCursor(expressionRefs.current[targetRow] ?? null, row.expression, token, (next) =>
      update(targetRow, { expression: next }),
    )
  }

  return (
    <div className="grid gap-3 rounded-lg border p-3">
      <span className="text-sm font-medium">Эффекты на материалы</span>
      <p className="text-muted-foreground text-xs">
        Выражение возвращает количество материала, которое добавится в смету. Ссылки на факты — через{' '}
        <code>{'{ключ}'}</code>. Пустое выражение — строка не сохраняется.
      </p>
      {targetIsChosenMaterial ? (
        <p className="text-muted-foreground text-xs">
          {fansOverSelection
            ? `Материал здесь не задаётся: каждый эффект применяется к каждому материалу, выбранному пользователем${
                categoryName ? ` из категории «${categoryName}»` : ''
              }, — результат × количество этого материала.`
            : targetFields.length > 0
              ? 'Материал здесь не задаётся: в записи повторителя несколько выборов материала — у каждого эффекта укажите, к какому из них он относится.'
              : `Материал здесь не задаётся: эффект применяется к материалу, который выберет пользователь${
                  categoryName ? ` из категории «${categoryName}»` : ''
                }.`}{' '}
          Кроме эффектов «с пунктом затрат»: они добавляют выбранный здесь пункт (например работу по
          укладке) вместе с выбранным материалом.
        </p>
      ) : null}

      {rows.length > 0 ? (
        <div className="bg-muted/40 grid gap-3 rounded-md border border-dashed p-2">
          <span className="text-muted-foreground text-xs">
            Вставка в выражение эффекта №{targetRow + 1} — щёлкните в нужное выражение, чтобы
            выбрать другое.
          </span>
          <KeyPalette palette={palette} onInsert={insert} />
          <AttributePalette
            attributes={targetAttributes.items}
            categoryName={targetAttributes.category}
            onInsert={insert}
          />
        </div>
      ) : null}

      {rows.map((row, index) => (
        <div key={index} className="grid gap-2 rounded-md border p-2">
          {targetIsChosenMaterial && row.fixedMaterial ? (
            <div className="grid gap-1 text-xs">
              <span className="font-medium">Пункт затрат вместе с выбранным материалом</span>
              <span className="text-muted-foreground">
                {fieldSchema
                  ? 'Добавляется для каждой записи, где выбран материал (в любом поле «Выбор»). Сам материал считают обычные эффекты.'
                  : `Добавляется, только когда материал${categoryName ? ` из категории «${categoryName}»` : ''} выбран${
                      fansOverSelection ? ' — для каждого выбранного: результат × его количество' : ''
                    }. В выражении доступны атрибуты выбранного материала.`}
              </span>
              <MaterialPicker
                materials={materials}
                selectedId={row.materialId}
                onSelect={(id) => update(index, { materialId: id })}
                ariaLabel={`Пункт затрат эффекта ${index + 1}`}
              />
            </div>
          ) : null}
          {targetIsChosenMaterial && row.fixedMaterial ? null : targetIsChosenMaterial &&
            targetFields.length > 0 ? (
            <label className="grid gap-1 text-xs">
              <span className="font-medium">К какому выбору материала относится эффект?</span>
              <Select
                value={
                  resolveTargetField(targetFields, currentTargetKey(row.targetFieldKey))?.key ?? ''
                }
                onChange={(event) => update(index, { targetFieldKey: event.target.value })}
                aria-label={`Выбор материала для эффекта ${index + 1}`}
              >
                {targetFields.map((field) => (
                  <option key={field.key} value={field.key}>
                    {field.label}
                    {field.category ? ` (категория: ${field.category})` : ''}
                  </option>
                ))}
              </Select>
            </label>
          ) : targetIsChosenMaterial ? (
            <p className="text-muted-foreground text-xs">
              Цель: выбранный материал{categoryName ? ` (категория: ${categoryName})` : ''}
            </p>
          ) : (
            <MaterialPicker
              materials={materials}
              selectedId={row.materialId}
              onSelect={(id) => update(index, { materialId: id })}
              ariaLabel={`Пункт затрат эффекта ${index + 1}`}
            />
          )}
          <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
            <Textarea
              ref={(element) => {
                expressionRefs.current[index] = element
              }}
              value={row.expression}
              onChange={(event) => update(index, { expression: event.target.value })}
              onFocus={() => setFocusedRow(index)}
              placeholder="{area} * 1.1"
              rows={2}
              className="font-mono"
              aria-label={`Выражение эффекта ${index + 1}`}
            />
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={() => onRows(rows.filter((_, position) => position !== index))}
              aria-label={`Удалить эффект ${index + 1}`}
            >
              <Trash2 />
            </Button>
          </div>
        </div>
      ))}

      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            onRows([...rows, { materialId: '', expression: '', targetFieldKey: '', fixedMaterial: false }])
          }
        >
          <Plus />
          Добавить эффект
        </Button>
        {targetIsChosenMaterial ? (
          // E.g. a tile question: the picked tile comes from its category, and the labor of laying it
          // is a fixed cost item added together with it.
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              onRows([...rows, { materialId: '', expression: '', targetFieldKey: '', fixedMaterial: true }])
            }
          >
            <Plus />
            Эффект материала с пунктом затрат
          </Button>
        ) : null}
      </div>
    </div>
  )
}

/**
 * The searchable, group-aware category picker for a question / repeater field. The editor keeps the
 * category by its CURRENT name (the id is resolved again on save), so this maps name ↔ id. A name
 * the catalog does not know stays selected as «… (нет в справочнике)» until another is picked.
 */
function CategoryNamePicker({
  categories,
  value,
  onChange,
  labelledBy,
  ariaLabel,
}: {
  categories: MaterialCategory[]
  /** Category name; '' = none. */
  value: string
  onChange: (name: string) => void
  labelledBy?: string
  ariaLabel?: string
}) {
  const selected = value ? (categories.find((category) => category.name === value) ?? null) : null
  return (
    <CategoryPicker
      categories={categories}
      selectedId={selected?.id ?? (value ? null : '')}
      buttonLabel={
        selected ? categoryLabel(selected) : value ? `${value} (нет в справочнике)` : '— не задана —'
      }
      onSelect={(id) => onChange(categories.find((category) => category.id === id)?.name ?? '')}
      labelledBy={labelledBy}
      ariaLabel={ariaLabel}
    />
  )
}
